import React, { useEffect, useState } from 'react';
import {
    Box,
    Collapse,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import { Business as CustomerIcon } from '@mui/icons-material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { fetchCustomers } from '../services/customerService';
import { useOrganization } from '../contexts/OrganizationContext';

const CustomerSection = ({ missionData, setMissionData, setIsDirty, handleFieldChange }) => {
    const { selectedOrg } = useOrganization();
    const [isExpanded, setIsExpanded] = useState(true);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadCustomers = async () => {
            if (!selectedOrg) {
                setError('No organization selected.');
                return;
            }

            setLoading(true);
            try {
                const data = await fetchCustomers(selectedOrg);
                setCustomers(data || []);
                setError(null);
            } catch (err) {
                setError('Failed to fetch customer data.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadCustomers();
    }, [selectedOrg]);

    useEffect(() => {
        setIsExpanded(!missionData?.completedAt);
    }, [missionData]);

    const handleCustomerChange = (event) => {
        const customerGuid = event.target.value;
        setIsDirty(true);
        setMissionData((prevData) => ({ ...prevData, customerGuid }));
        if (handleFieldChange) handleFieldChange('customerGuid', customerGuid);
    };

    return (
        <Box
            sx={{
                backgroundColor: '#ffffff',
                border: '1px solid #dcdcdc',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
        >
            {/* Section Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    marginBottom: isExpanded ? '16px' : '0px',
                }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <CustomerIcon sx={{ color: '#42a5f5', marginRight: '8px' }} />
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Customer
                </Typography>
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>

            {/* Expandable Content */}
            <Collapse in={isExpanded}>
                {loading ? (
                    <Typography variant="body2" sx={{ color: '#6c757d', textAlign: 'center' }}>
                        Loading customers...
                    </Typography>
                ) : error ? (
                    <Typography variant="body2" sx={{ color: '#e74c3c', textAlign: 'center' }}>
                        {error}
                    </Typography>
                ) : (
                    <FormControl fullWidth sx={{ marginBottom: '16px' }} variant="outlined">
                        <InputLabel id="customer-select-label" shrink>
                            Select Customer
                        </InputLabel>
                        <Select
                            labelId="customer-select-label"
                            value={missionData.customerGuid || ''}
                            onChange={handleCustomerChange}
                            displayEmpty
                            label="Select Customer"
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {customers.map((customer) => (
                                <MenuItem key={customer.customerGuid} value={customer.customerGuid}>
                                    {customer.businessName || 'Unnamed Customer'}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                <Typography variant="body2" sx={{ color: '#9e9e9e', paddingLeft: '0px', fontSize: '0.8rem' }}>
                    Assign a customer to this mission for billing purposes.
                </Typography>
            </Collapse>
        </Box>
    );
};

export default CustomerSection;